import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Plus, Store, DollarSign, Eye, Package, ArrowRight, Send, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ['Memory', 'Thinking', 'Career', 'Communication', 'Behavior', 'General'];

export default function CreatorDashboard() {
  const { user } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', category: 'General', price: 9.99 });
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [sessionForm, setSessionForm] = useState({ title: '', content: '' });
  const [programSessions, setProgramSessions] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [progsRes, revRes] = await Promise.all([
        axios.get(`${API}/creator/programs`, { withCredentials: true }),
        axios.get(`${API}/creator/revenue`, { withCredentials: true })
      ]);
      setPrograms(progsRes.data);
      setRevenue(revRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const createProgram = async () => {
    try {
      const res = await axios.post(`${API}/creator/programs`, form, { withCredentials: true });
      setPrograms(prev => [res.data, ...prev]);
      setShowForm(false);
      setForm({ title: '', description: '', category: 'General', price: 9.99 });
      toast.success('Program created!');
    } catch (err) { toast.error('Failed to create program'); }
  };

  const publishProgram = async (programId) => {
    try {
      await axios.post(`${API}/creator/programs/${programId}/publish`, {}, { withCredentials: true });
      toast.success('Program published to marketplace!');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to publish'); }
  };

  const selectProgram = async (prog) => {
    setSelectedProgram(prog);
    try {
      const res = await axios.get(`${API}/creator/programs/${prog.program_id}/sessions`, { withCredentials: true });
      setProgramSessions(res.data);
    } catch { setProgramSessions([]); }
  };

  const addSession = async () => {
    if (!selectedProgram || !sessionForm.title) return;
    try {
      let contentJson = { phases: [] };
      if (sessionForm.content.trim()) {
        try { contentJson = JSON.parse(sessionForm.content); } catch { toast.error('Invalid JSON content'); return; }
      }
      const res = await axios.post(
        `${API}/creator/programs/${selectedProgram.program_id}/sessions`,
        { title: sessionForm.title, content: contentJson },
        { withCredentials: true }
      );
      setProgramSessions(prev => [...prev, res.data]);
      setSessionForm({ title: '', content: '' });
      toast.success('Session added!');
    } catch (err) { toast.error('Failed to add session'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="creator-loading">
        <div className="mono-data text-lg animate-pulse-red">Loading Creator Portal...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="creator-dashboard">
      {/* Header */}
      <div className="border-b border-[#09090B] py-8">
        <div className="container-swiss flex items-center justify-between">
          <div>
            <p className="mono-data text-[#09090B] mb-1">Creator Portal</p>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="creator-heading">
              Your Programs
            </h1>
          </div>
          <div className="flex gap-3">
            <Link to="/marketplace" className="btn-secondary text-xs px-4 py-2 flex items-center gap-2" data-testid="view-marketplace-btn">
              <Eye className="w-3 h-3" /> Marketplace
            </Link>
            <button onClick={() => setShowForm(true)} className="btn-accent text-xs px-4 py-2 flex items-center gap-2" data-testid="new-program-btn">
              <Plus className="w-3 h-3" /> New Program
            </button>
          </div>
        </div>
      </div>

      {/* Revenue Stats */}
      {revenue && (
        <div className="border-b border-[#09090B]">
          <div className="container-swiss">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-l border-t border-[#09090B]">
              {[
                { label: 'Revenue (70%)', value: `$${revenue.total_revenue}`, icon: DollarSign },
                { label: 'Total Sales', value: revenue.total_purchases, icon: Package },
                { label: 'Published', value: revenue.published_programs, icon: Send },
                { label: 'Total Programs', value: revenue.total_programs, icon: Store },
              ].map(s => (
                <div key={s.label} className="grid-border-item" data-testid={`rev-${s.label.split(' ')[0].toLowerCase()}`}>
                  <div className="label-swiss mb-2 flex items-center gap-1"><s.icon className="w-3 h-3" /> {s.label}</div>
                  <div className="text-3xl font-black tracking-tighter" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Create Program Form */}
      {showForm && (
        <div className="border-b border-[#09090B]">
          <div className="container-swiss py-8">
            <h2 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Create New Program</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Program title" className="input-swiss text-base" data-testid="form-title" />
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="input-swiss text-base" data-testid="form-category">
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Description" className="input-swiss text-base md:col-span-2" data-testid="form-description" />
              <input type="number" value={form.price} onChange={e => setForm(f => ({ ...f, price: parseFloat(e.target.value) || 0 }))} placeholder="Price ($)" className="input-swiss text-base" data-testid="form-price" />
            </div>
            <div className="flex gap-3 mt-4">
              <button onClick={createProgram} disabled={!form.title} className="btn-primary text-xs px-6 py-2 disabled:opacity-40" data-testid="form-submit">Create</button>
              <button onClick={() => setShowForm(false)} className="btn-secondary text-xs px-6 py-2" data-testid="form-cancel">Cancel</button>
            </div>
          </div>
        </div>
      )}

      <div className="container-swiss py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Programs List */}
          <div className="lg:col-span-1">
            <h3 className="mono-data text-sm mb-4">Your Programs</h3>
            {programs.length === 0 ? (
              <div className="border border-[#09090B] p-6 text-center">
                <p className="text-zinc-500 text-sm">No programs yet. Create your first!</p>
              </div>
            ) : (
              <div className="space-y-2">
                {programs.map(p => (
                  <button
                    key={p.program_id}
                    onClick={() => selectProgram(p)}
                    className={`w-full text-left border p-4 transition-colors ${selectedProgram?.program_id === p.program_id ? 'border-[#09090B] bg-zinc-100' : 'border-[#09090B] hover:bg-zinc-50'}`}
                    data-testid={`prog-select-${p.program_id}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>{p.title}</span>
                      <span className={`mono-data text-xs ${p.status === 'published' ? 'text-[#09090B]' : 'text-zinc-400'}`}>{p.status}</span>
                    </div>
                    <div className="mono-data text-xs text-zinc-400">{p.category} &middot; ${p.price} &middot; {p.session_count} sessions</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Session Manager */}
          <div className="lg:col-span-2">
            {selectedProgram ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="mono-data text-sm">Sessions: {selectedProgram.title}</h3>
                  {selectedProgram.status === 'draft' && programSessions.length > 0 && (
                    <button onClick={() => publishProgram(selectedProgram.program_id)} className="btn-accent text-xs px-4 py-2 flex items-center gap-2" data-testid="publish-btn">
                      <Send className="w-3 h-3" /> Publish
                    </button>
                  )}
                </div>

                {/* Existing Sessions */}
                {programSessions.length > 0 && (
                  <div className="border border-[#09090B] mb-4">
                    {programSessions.map((s, i) => (
                      <div key={s.session_id} className={`p-4 flex items-center justify-between ${i < programSessions.length - 1 ? 'border-b border-[#09090B]' : ''}`}>
                        <div className="flex items-center gap-3">
                          <span className="mono-data text-xs text-zinc-400">{String(s.order).padStart(2, '0')}</span>
                          <span className="text-sm font-medium">{s.title}</span>
                        </div>
                        <span className="mono-data text-xs text-zinc-400">{s.content?.phases?.length || 0} phases</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Session Form */}
                <div className="border border-[#09090B] p-4">
                  <h4 className="mono-data text-xs mb-3">Add Session</h4>
                  <input value={sessionForm.title} onChange={e => setSessionForm(f => ({ ...f, title: e.target.value }))} placeholder="Session title" className="input-swiss text-sm mb-3" data-testid="session-title-input" />
                  <textarea value={sessionForm.content} onChange={e => setSessionForm(f => ({ ...f, content: e.target.value }))} placeholder='{"phases": [{"phase_id": "p1", "title": "Phase 1", "blocks": [...]}]}' className="input-swiss text-xs h-24 resize-none border-2 border-[#09090B] p-3 w-full font-mono" data-testid="session-content-input" />
                  <div className="flex justify-between items-center mt-3">
                    <span className="text-xs text-zinc-400">JSON content (optional). Leave empty for placeholder.</span>
                    <button onClick={addSession} disabled={!sessionForm.title} className="btn-primary text-xs px-4 py-2 disabled:opacity-40" data-testid="add-session-btn">
                      Add Session
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="border border-[#09090B] p-12 text-center">
                <Store className="w-8 h-8 text-zinc-300 mx-auto mb-4" />
                <p className="text-zinc-500 text-sm">Select a program to manage its sessions</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
